import firebase from 'firebase/compat/app';
import { RefObject } from 'react';
import { Song } from '../models/Song';
import { app } from "../services/LoginService";
import { MediaUrlService } from './MediaUrlService';
import { isProjectStorageUrl } from '../util/mediaRef';
declare const window: any;

const AudioContext = window.AudioContext || window.webkitAudioContext;

export default class MediaService {
  private static instance: MediaService;
  private context!: AudioContext;
  private videoElement!: HTMLVideoElement;
  private mediaSourceNode!: MediaElementAudioSourceNode;
  private analyzerNode!: AnalyserNode;
  private initialized: boolean = false;
  private frequencyData: Uint8Array[] = [];
  private waveFormData: Uint8Array[] = [];
  private frequencyBufferIndex: number = 0;
  private waveFormBufferIndex: number = 0;
  public progress = 0;
  public buffer = 0;
  public nowPlaying: string = '';
  private userVolume: number = 1;
  private currentSong?: Song;
  private lastRecoveryAt = 0;
  private constructor() {
    try {
      const stored = localStorage.getItem('song-share:volume');
      if (stored !== null) {
        const parsed = Number(stored);
        if (!Number.isNaN(parsed)) {
          this.userVolume = Math.min(1, Math.max(0, parsed));
        }
      }
    } catch { /* localStorage unavailable */ }
  }

  public setVolume(value: number) {
    this.userVolume = Math.min(1, Math.max(0, value));
    if (this.videoElement) {
      this.videoElement.volume = this.userVolume;
    }
  }

  public getVolume() {
    return this.userVolume;
  }

  public static please(): MediaService {
    if (!MediaService.instance) {
      MediaService.instance = new MediaService();
    }
    return MediaService.instance;
  }

  public setPlayerRef(playerRef: RefObject<HTMLVideoElement>) {
    if (playerRef.current) {
      if (this.videoElement) {
        this.videoElement.removeEventListener('error', this.onMediaError);
      }
      this.videoElement = playerRef.current;
      this.videoElement.addEventListener('error', this.onMediaError);
    }
    if (window.AudioContext) {
      this.context = new AudioContext();
    }
  }

  public seek(percentage: number) {
    this.videoElement.currentTime = percentage * this.videoElement.duration
  }
  public isPlaying() {
   return !this.videoElement.paused
  }

  public updateBuffer() {
    const videoElement = this.videoElement;
    const time = videoElement.currentTime;
    const duration = videoElement.duration;
    this.progress = time / duration;
    if (duration > 0) {
      try {
        const bufferedEnd = videoElement.buffered.end(videoElement.buffered.length - 1);
        this.buffer = bufferedEnd / duration;
      } catch {
        this.buffer = 0;
      }
    }
  }

  public pause() {
    this.videoElement.pause();
  }

  public async play() {
    await this.videoElement.play();
  }

  public getContext() {
    return this.context;
  }

  public getVideoElement() {
    return this.videoElement;
  }

  public getMediaSourceNode() {
    return this.mediaSourceNode;
  }

  public getAnalyzerNode() {
    return this.analyzerNode;
  }

  public getDestinationNode() {
    return this.getDestinationNode;
  }

  public async setSong(song: Song) {
    console.log("Setting song",song);
    firebase.analytics(app).logEvent("play_song", { id: song.id });
    if (this.nowPlaying === song.id) {
      return;
    } else {
      this.nowPlaying = song.id;
    }
    this.currentSong = song;
    if (this.videoElement) {
      // Silence and pause the previous song immediately, synchronously,
      // before any `await` below. getMediaUrl (S01) means switching songs
      // can now involve a real network round trip; if the old song isn't
      // stopped until *after* that resolves, a racing play() call (e.g. a
      // caller that doesn't wait for setSong's promise) can audibly resume
      // it from wherever it was left for the duration of that round trip.
      this.videoElement.volume = 0;
      this.videoElement.pause();
      // S01: non-public recordings are stored as token-less gs:// refs and
      // must be exchanged for a short-lived signed URL (getMediaUrl). Public
      // / not-yet-migrated songs resolve synchronously to their stored URL.
      let url = MediaUrlService.peek(song);
      if (!url) {
        try {
          url = await MediaUrlService.resolve(song);
        } catch (error) {
          if (this.nowPlaying === song.id) {
            this.nowPlaying = '';
          }
          throw error;
        }
        // Another song was picked while we were waiting.
        if (this.nowPlaying !== song.id) {
          return;
        }
      }
      this.videoElement.volume = 0;
      if (this.videoElement.src !== url) {
        this.videoElement.src = url;
      }
      this.videoElement.volume = this.userVolume;
    }
  }

  /**
   * The media element failed to load (e.g. 403 because a signed URL expired
   * after its 15-minute window, or the song's download token was revoked when
   * it was made non-public). Ask getMediaUrl for a fresh authorized URL and
   * resume where playback stopped. Throttled so a genuinely unauthorized or
   * broken file doesn't loop.
   */
  private onMediaError = async () => {
    const song = this.currentSong;
    const el = this.videoElement;
    if (!song || !el || !isProjectStorageUrl(song.media?.src)) {
      return;
    }
    if (Date.now() - this.lastRecoveryAt < 60 * 1000) {
      return;
    }
    this.lastRecoveryAt = Date.now();
    const resumeAt = el.currentTime || 0;
    const resume = !el.paused;
    try {
      const url = await MediaUrlService.resolve(song, 'playback', true);
      if (this.currentSong !== song || el.src === url) {
        return;
      }
      el.src = url;
      if (resumeAt > 0) {
        el.addEventListener('loadedmetadata', () => {
          el.currentTime = resumeAt;
        }, { once: true });
      }
      if (resume) {
        await el.play();
      }
    } catch (error) {
      console.error('Could not refresh media URL', error);
    }
  }

  public initializeAnalysis() {
    if (!this.initialized) {
      this.mediaSourceNode = this.context.createMediaElementSource(this.videoElement);
      this.analyzerNode = this.context.createAnalyser();
      this.mediaSourceNode.connect(this.analyzerNode);
      this.analyzerNode.connect(this.context.destination);
      this.initialized = true;
      this.frequencyData.push(new Uint8Array(this.analyzerNode.frequencyBinCount));
      this.waveFormData.push(new Uint8Array(this.analyzerNode.fftSize));
      this.context.resume();
    }
    return this.initialized;
  }

  public getWaveFormBufferSize() {
    return this.waveFormData.length * this.analyzerNode.fftSize;
  }

  public getLatestFrequencyData() {
    this.analyzerNode.getByteFrequencyData(this.frequencyData[this.frequencyBufferIndex]);
    this.frequencyBufferIndex = (this.frequencyBufferIndex + 1) % this.frequencyData.length;
  }

  public getLatestWaveFormData() {
    this.analyzerNode.getByteTimeDomainData(this.waveFormData[this.waveFormBufferIndex]);
    this.waveFormBufferIndex = (this.waveFormBufferIndex + 1) % this.waveFormData.length;
  }

  public getFrequencyBuffers() {
    return this.frequencyData;
  }

  public getWaveBuffers() {
    return this.waveFormData;
  }

  public getActiveFrequencyBufferIndex() {
    return this.frequencyBufferIndex;
  }

  public getActiveWaveBufferIndex() {
    return this.waveFormBufferIndex;
  }

  public getCircularWaveBufferSize() {
    return this.analyzerNode.fftSize * this.waveFormData.length;
  }

  public beReady() {
    return this.initialized;
  }

  public mute() {
    if (this.videoElement) {
      this.videoElement.muted = true;
    }
  }

  public unmute() {
    if (this.videoElement) {
      this.videoElement.muted = false;
    }
  }

  public isMuted(): boolean {
    return this.videoElement ? this.videoElement.muted : false;
  }
}
